<template>
  <div class="sellers-container">
    <!-- Cabeçalho da página -->
    <div class="page-header">
      <h1 class="page-title">
        <i class="fa-solid fa-users"></i>
        Cobradores
      </h1>
      <p class="page-subtitle">Gerencie os cobradores e suas configurações</p>
    </div>

    <!-- Navegação por tabs -->
    <div class="tabs-container">
      <nav class="modern-tabs">
        <button 
          class="tab-button" 
          :class="{ active: selectedTab === 1 }" 
          @click="selectedTab = 1"
        >
          <i class="fa-solid fa-list"></i>
          LISTAGEM
        </button>
        <button 
          class="tab-button" 
          :class="{ 
            active: selectedTab === 2,
            disabled: !canCreateEditDeleteCollectors 
          }" 
          @click="canCreateEditDeleteCollectors ? (collector.id ? selectedTab = 2 : createCollector()) : showPermissionError()"
          :disabled="!canCreateEditDeleteCollectors"
          :title="!canCreateEditDeleteCollectors ? 'Você não tem permissão para criar/editar cobradores' : ''"
        >
          <i class="fa-solid fa-plus tab-plus-icon"></i>
          {{ collector.id ? 'EDITAR' : 'CADASTRO' }}
        </button>
      </nav>
    </div>

    <!-- Loading -->
    <div v-show="loading" class="loading-container">
      <div class="loading-spinner">
        <i class="fa-solid fa-spinner fa-spin"></i>
        <span>Carregando...</span>
      </div>
    </div>

    <!-- Tab 1: Listagem -->
    <div v-show="selectedTab === 1 && !loading" class="tab-content">
      <div class="table-card">
        <!-- Cabeçalho da tabela -->
        <div class="table-header">
          <div class="search-container">
            <div class="search-input-group">
              <i class="fa-solid fa-search search-icon"></i>
              <input 
                type="text" 
                v-model="quickFilter" 
                class="search-input" 
                placeholder="Pesquisar por username ou nome..." 
                autocomplete="off"
              />
            </div>
          </div>
          <span v-if="totalRows > 0" class="table-count">({{ totalRows }} cobrador(es) encontrado(s))</span>
          <div v-if="totalPages > 1" class="pagination-info">
            <span>Página {{ pageIndex }} de {{ totalPages }}</span>
            <div class="pagination-controls">
              <button 
                class="btn-pagination" 
                @click="changePage(pageIndex - 1)"
                :disabled="pageIndex <= 1"
              >
                <i class="fa-solid fa-chevron-left"></i>
              </button>
              <button 
                class="btn-pagination" 
                @click="changePage(pageIndex + 1)"
                :disabled="pageIndex >= totalPages"
              >
                <i class="fa-solid fa-chevron-right"></i>
              </button>
            </div>
          </div>
          <button type="button" class="btn-primary" @click="createCollector()" v-if="canCreateEditDeleteCollectors">
            <i class="fa-solid fa-plus"></i>
            Adicionar Cobrador
          </button>
        </div>

        <!-- Mensagem informativa para usuários com permissões limitadas -->
        <div v-if="!isAdmin" class="permission-info">
          <div class="info-card">
            <i class="fa-solid fa-info-circle"></i>
            <div class="info-content">
              <span v-if="isBanca">
                <strong>Modo BANCA:</strong> Você pode visualizar apenas os cobradores de suas regiões e gerenciar cobradores (criar/editar/excluir).
                <span v-if="currentSelectedRegion"> Exibindo cobradores da região: <strong>{{ currentSelectedRegion.name }}</strong></span>
              </span>
              <span v-else-if="isGerente">
                <strong>Modo GERENTE:</strong> Você pode visualizar apenas os cobradores de suas regiões e gerenciar cobradores (criar/editar/excluir).
                <span v-if="currentSelectedRegion"> Exibindo cobradores da região: <strong>{{ currentSelectedRegion.name }}</strong></span>
              </span>
              <span v-else-if="isCobrador">
                <strong>Modo COBRADOR:</strong> Você pode visualizar apenas suas próprias informações. Criação, edição e exclusão não são permitidas.
              </span>
            </div>
          </div>
        </div>

        <!-- Mensagem informativa para admin -->
        <div v-if="isAdmin" class="permission-info">
          <div class="info-card">
            <i class="fa-solid fa-info-circle"></i>
            <div class="info-content">
              <span v-if="currentSelectedBanca && currentSelectedRegion">
                <strong>Filtros Ativos:</strong> Exibindo cobradores da banca <strong>{{ currentSelectedBanca.name }}</strong> 
                e região <strong>{{ currentSelectedRegion.name }}</strong>.
              </span>
              <span v-else-if="currentSelectedBanca && !currentSelectedRegion">
                <strong>Filtro por Banca:</strong> Exibindo todos os cobradores da banca <strong>{{ currentSelectedBanca.name }}</strong>. 
                Para filtrar por região específica, selecione uma região no filtro.
              </span>
              <span v-else-if="!currentSelectedBanca && currentSelectedRegion">
                <strong>Filtro por Região:</strong> Exibindo cobradores da região <strong>{{ currentSelectedRegion.name }}</strong>. 
                Para filtrar por banca específica, selecione uma banca no filtro.
              </span>
              <span v-else>
                <strong>Visualização Global:</strong> Exibindo todos os cobradores do sistema. 
                Para filtrar, selecione uma banca e/ou região nos filtros localizados no topo da página.
              </span>
            </div>
          </div>
        </div>

        <!-- Tabela -->
        <div class="table-container">
          <table class="modern-table">
            <thead>
              <tr>
                <th style="text-align: center;">Username</th>
                <th style="text-align: center;">Telefone</th>
                <th style="text-align: center;">Banca</th>
                <th style="text-align: center;">Região</th>
                <th style="text-align: center;">Status</th>
                <th style="text-align: center;" width="140">Ações</th>
              </tr>
            </thead>
            <tbody>
              <tr 
                v-for="c in paginatedCollectors" 
                :key="c.id"
                class="table-row"
              >
                <td style="text-align: center;">
                  <span class="seller-name">{{ c.username }}</span>
                </td>
                <td style="text-align: center;">{{ c.phone || '-' }}</td>
                <td style="text-align: center;">{{ c.bancaName || '-' }}</td>
                <td style="text-align: center;">{{ c.regionName || '-' }}</td>
                <td style="text-align: center;">
                  <span :class="['status-badge', c.active ? 'badge-success' : 'badge-danger']">
                    {{ c.active ? 'Ativo' : 'Inativo' }}
                  </span>
                </td>
                <td style="text-align: center;">
                  <div class="action-buttons">
                    <button 
                      class="btn-action btn-edit" 
                      @click="editCollector(c)"
                      title="Editar"
                      v-if="canCreateEditDeleteCollectors"
                    >
                      <i class="fa-solid fa-pen-to-square"></i>
                    </button>
                    <button 
                      class="btn-action btn-delete" 
                      @click="deleteCollector(c.id)"
                      title="Excluir"
                      v-if="canCreateEditDeleteCollectors"
                    >
                      <i class="fa-solid fa-trash"></i>
                    </button>
                  </div>
                </td>
              </tr>
              <tr v-if="filteredCollectors.length === 0" class="no-data">
                <td colspan="6">
                  <div class="no-data-message">
                    <i class="fa-solid fa-users"></i>
                    <div class="no-data-content">
                      <span class="no-data-title">Nenhum cobrador encontrado</span>
                      <span v-if="currentSelectedBanca || currentSelectedRegion || quickFilter" class="no-data-subtitle">
                        <span v-if="currentSelectedBanca">Banca: {{ currentSelectedBanca.name }}</span>
                        <span v-if="currentSelectedRegion"> • Região: {{ currentSelectedRegion.name }}</span>
                        <span v-if="quickFilter"> • Pesquisa: "{{ quickFilter }}"</span>
                      </span>
                      <span v-else class="no-data-subtitle">Nenhum cobrador cadastrado no sistema</span>
                    </div>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div v-if="totalPages > 1" class="pagination-footer">
          <div class="pagination-info">
            <span>Mostrando {{ startItem }}-{{ endItem }} de {{ totalRows }} resultados</span>
          </div>
          <div class="pagination-controls">
            <button 
              class="btn-pagination" 
              @click="changePage(1)"
              :disabled="pageIndex <= 1"
            >
              <i class="fa-solid fa-angles-left"></i>
            </button>
            <button 
              class="btn-pagination" 
              @click="changePage(pageIndex - 1)"
              :disabled="pageIndex <= 1"
            >
              <i class="fa-solid fa-chevron-left"></i>
            </button>
            <span class="page-info">{{ pageIndex }} / {{ totalPages }}</span>
            <button 
              class="btn-pagination" 
              @click="changePage(pageIndex + 1)"
              :disabled="pageIndex >= totalPages"
            >
              <i class="fa-solid fa-chevron-right"></i>
            </button>
            <button 
              class="btn-pagination" 
              @click="changePage(totalPages)"
              :disabled="pageIndex >= totalPages"
            >
              <i class="fa-solid fa-angles-right"></i>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Tab 2: Formulário -->
    <div v-show="selectedTab === 2" class="tab-content">
      <div class="form-card">
        <div class="form-header">
          <h3 class="form-title">
            <i class="fa-solid fa-users"></i>
            {{ collector.id ? 'Editar Cobrador' : 'Novo Cobrador' }}
            <p class="form-subtitle">
              {{ collector.id ? 'Atualize as informações do cobrador' : 'Preencha as informações do novo cobrador' }}
            </p>
          </h3>
        </div>

        <div class="form-body">
          <div class="form-grid">
            <div class="input-group">
              <label class="input-label required">
                <i class="fa-solid fa-user"></i>
                Nome
              </label>
              <input 
                class="form-input" 
                type="text" 
                v-model="collector.name"
                placeholder="Digite o nome completo..."
              />
            </div>

            <div class="input-group">
              <label class="input-label required">
                <i class="fa-solid fa-at"></i>
                Usuário
              </label>
              <input 
                class="form-input" 
                type="text" 
                v-model="collector.username"
                placeholder="Digite o nome de usuário..."
                autocomplete="off"
              />
            </div>

            <div class="input-group">
              <label class="input-label">
                <i class="fa-solid fa-phone"></i>
                Telefone
              </label>
              <input 
                class="form-input" 
                type="text" 
                v-model="collector.phone"
                placeholder="Digite o telefone..."
              />
            </div>

            <div class="input-group">
              <label class="input-label">
                <i class="fa-solid fa-user-tag"></i>
                Cargo
              </label>
              <input 
                class="form-input" 
                type="text" 
                value="COBRADOR"
                disabled style="background-color: rgb(230 54 54 / 9%);"
              />
            </div>

            <!-- Campo de Banca -->
            <div class="input-group">
              <label class="input-label">
                <i class="fa-solid fa-building"></i>
                Banca (Auto-preenchida)
              </label>
              <input 
                class="form-input banca-auto-filled" 
                type="text" 
                :value="getBancaName(collector.bancaId)"
                disabled
                readonly
                placeholder="Nenhuma banca associada à região"
              />
              <small class="form-help-text">
                <i class="fa-solid fa-magic"></i>
                A banca é automaticamente preenchida baseada na região selecionada.
              </small>
            </div>

            <div class="input-group">
              <label class="input-label" :class="{ required: !collector.id }">
                <i class="fa-solid fa-lock"></i>
                {{ collector.id ? 'Nova Senha (opcional)' : 'Senha' }}
              </label>
              <input 
                class="form-input" 
                type="password" 
                v-model="collector.password"
                :placeholder="collector.id ? 'Digite a nova senha (deixe vazio para manter a atual)...' : 'Digite a senha...'"
                autocomplete="new-password"
              />
              <small v-if="collector.id" class="input-help">
                <i class="fa-solid fa-info-circle"></i>
                Deixe vazio para manter a senha atual
              </small>
            </div>

            <!-- Região Ativa (somente leitura) -->
            <div class="input-group full-width">
              <label class="input-label">
                <i class="fa-solid fa-map-location-dot"></i>
                Região
              </label>
              <div class="region-info-container">
                <div v-if="currentSelectedRegion" class="region-info-display">
                  <div class="active-region">
                    <i class="fa-solid fa-check-circle"></i>
                    <span class="region-name">{{ currentSelectedRegion.name }}</span>
                    <span class="region-status">Região Ativa</span>
                  </div>
                  <small class="region-help">
                    <i class="fa-solid fa-info-circle"></i>
                    O cobrador será automaticamente associado à região ativa selecionada.
                  </small>
                </div>
                
                <div v-else class="no-region-warning">
                  <div class="warning-content">
                    <i class="fa-solid fa-exclamation-triangle"></i>
                    <div class="warning-text">
                      <strong>Nenhuma região ativa selecionada</strong>
                      <p>É necessário selecionar uma região ativa no filtro para criar um cobrador.</p>
                    </div>
                  </div>
                  <button 
                    type="button" 
                    class="btn-select-region"
                    @click="showRegionSelectionMessage"
                  >
                    <i class="fa-solid fa-map-location-dot"></i>
                    Como selecionar uma região?
                  </button>
                </div>
              </div>
            </div>

            <div v-if="collector.id" class="input-group">
              <label class="input-label">
                <i class="fa-solid fa-toggle-on"></i>
                Status
              </label>
              <select v-model="collector.active" class="form-input">
                <option :value="true">Ativo</option>
                <option :value="false">Inativo</option>
              </select>
            </div>
          </div>
        </div>

        <div class="form-footer">
          <button 
            type="button" 
            class="btn-primary" 
            @click="collector.id ? updateCollector() : insertCollector()"
            :disabled="saving || (!collector.id && !currentSelectedRegion)"
          >
            <i v-if="saving" class="fa-solid fa-spinner fa-spin"></i>
            <i v-else class="fa-solid fa-save"></i>
            {{ saving ? 'Salvando...' : (collector.id ? 'Atualizar' : 'Salvar') }}
          </button>
          <button 
            type="button" 
            class="btn-secondary" 
            @click="reset()"
          >
            <i class="fa-solid fa-times"></i>
            Cancelar
          </button>
        </div>
      </div>
    </div>


  </div>
</template>

<script>
import mainService from "@/services/mainService";
import { toast } from "@/composables/useToast";
import { handleErrorMessage } from "@/services/config";
import { ROLES } from "@/services/permissions";

export default {
  name: "ViewCollectors",
  data: () => ({
    quickFilter: "",
    collectors: [],
    collector: {},
    regions: [],
    bancas: [], // Adicionar array de bancas
    selectedTab: 1,
    pageIndex: 1,
    pageSize: 10,
    loading: true,
    saving: false,
    currentUser: null,
    userResponsibleRegions: [],
    selectedRegionSnapshot: null,
    selectedBancaSnapshot: null
  }),
  computed: {
    currentSelectedRegion() {
      return this.selectedRegionSnapshot || this.readSelectedRegionFromStorage();
    },

    currentSelectedBanca() {
      return this.selectedBancaSnapshot || this.readSelectedBancaFromStorage();
    },

    filteredCollectors() {
      let collectors = this.collectors;
      
      // Aplicar filtro baseado no role do usuário
      if (this.isCobrador) {
        // COBRADOR só vê a si mesmo
        collectors = collectors.filter(collector => 
          collector.id === this.currentUser.id
        );
      } else if (!this.isAdmin) {
        // BANCA e GERENTE veem apenas cobradores de suas regiões (cobradores só têm região principal)
        collectors = collectors.filter(collector => 
          this.userResponsibleRegions.includes(collector.regionId)
        );
        
        // Para usuários não-admin, aplicar filtro por região ativa selecionada se houver
        if (this.currentSelectedRegion) {
          collectors = collectors.filter(collector => {
            // Cobradores só têm região principal, então verificar apenas essa
            return String(collector.regionId) === String(this.currentSelectedRegion.id);
          });
        }
      } else if (this.isAdmin) {
        // ADMIN: aplicar filtro por banca selecionada primeiro
        if (this.currentSelectedBanca) {
          // Filtrar cobradores pela banca selecionada
          const beforeBancaFilter = collectors.length;
          collectors = collectors.filter(collector => {
            return String(collector.bancaId) === String(this.currentSelectedBanca.id);
          });
        }
        
        // Em seguida, aplicar filtro por região apenas se houver região selecionada
        if (this.currentSelectedRegion) {
          const beforeRegionFilter = collectors.length;
          collectors = collectors.filter(collector => {
            // Cobradores só têm região principal, então verificar apenas essa
            return String(collector.regionId) === String(this.currentSelectedRegion.id);
          });
        }
        // Se currentSelectedRegion é null, não aplica filtro de região (mostra todos)
      }
      
      // Aplicar filtro de pesquisa
      if (this.quickFilter) {
        const beforeSearchFilter = collectors.length;
        const filter = this.quickFilter.toUpperCase();
        collectors = collectors.filter(collector => 
          collector.username.toUpperCase().includes(filter) ||
          collector.name.toUpperCase().includes(filter) ||
          (filter === "ATIVO" && collector.active) ||
          (filter === "INATIVO" && !collector.active)
        );
      }
      
      return collectors;
    },

    totalRows() {
      return this.filteredCollectors.length;
    },

    totalPages() {
      return Math.ceil(this.totalRows / this.pageSize) || 0;
    },

    startItem() {
      return this.totalRows === 0 ? 0 : (this.pageIndex - 1) * this.pageSize + 1;
    },

    endItem() {
      if (this.totalRows === 0) return 0;
      return Math.min(this.pageIndex * this.pageSize, this.totalRows);
    },

    paginatedCollectors() {
      const start = (this.pageIndex - 1) * this.pageSize;
      return this.filteredCollectors.slice(start, start + this.pageSize);
    },

    isAdmin() {
      return this.currentUser && this.currentUser.role === ROLES.ADMIN;
    },

    isBanca() {
      return this.currentUser && this.currentUser.role === ROLES.BANCA;
    },

    isGerente() {
      return this.currentUser && this.currentUser.role === ROLES.GERENTE;
    },

    isCobrador() {
      return this.currentUser && this.currentUser.role === ROLES.COBRADOR;
    },

    canCreateEditDeleteCollectors() {
      // ADMIN: acesso total
      // BANCA e GERENTE: podem criar/editar/excluir
      // COBRADOR: não pode criar/editar/excluir
      return this.isAdmin || this.isBanca || this.isGerente;
    },

    availableRegionsForUser() {
      if (this.isAdmin) {
        return this.regions;
      } else {
        // Para BANCA, GERENTE e COBRADOR, filtrar apenas as regiões que o usuário tem cadastradas
        return this.regions.filter(region => 
          this.userResponsibleRegions.includes(region.id)
        );
      }
    },

    availableBancasForUser() {
      if (this.isAdmin) {
        // ADMIN vê todas as bancas
        return this.bancas;
      } else if (this.isBanca) {
        // BANCA vê apenas ela mesma
        return this.bancas.filter(banca => 
          banca.responsibleUserIds && banca.responsibleUserIds.includes(this.currentUser.id)
        );
      } else {
        // GERENTE e COBRADOR: mostrar bancas das regiões onde eles atuam
        return this.bancas.filter(banca => {
          if (!banca.regionIds) return false;
          return banca.regionIds.some(regionId => 
            this.userResponsibleRegions.includes(regionId)
          );
        });
      }
    },



  },
  async mounted() {
    this.syncSelectedRegionSnapshot();
    this.syncSelectedBancaSnapshot();
    window.addEventListener('regionChanged', this.onRegionOrBancaChanged);
    window.addEventListener('regionAutoSelected', this.onRegionOrBancaChanged);
    window.addEventListener('bancaChanged', this.onRegionOrBancaChanged);
    window.addEventListener('storage', this.onStorageChanged);

    this.loading = true;
    try {
      await this.loadCurrentUser();
      await this.initializeFilters(); // Carrega regiões
      await this.loadBancas(); // Carrega bancas
      // Para usuários BANCA, complementar regiões responsáveis com regiões das bancas
      if (this.isBanca) {
        await this.loadBancaRegions();
      }
      await this.searchCollectors(); // Carrega cobradores (precisa das regiões e bancas)
    } catch (error) {
      this.loading = false;
      toast.error(handleErrorMessage(error));
    }
  },
  beforeUnmount() {
    window.removeEventListener('regionChanged', this.onRegionOrBancaChanged);
    window.removeEventListener('regionAutoSelected', this.onRegionOrBancaChanged);
    window.removeEventListener('bancaChanged', this.onRegionOrBancaChanged);
    window.removeEventListener('storage', this.onStorageChanged);
  },
  watch: {
    quickFilter() {
      this.pageIndex = 1;
    },
    totalPages(newTotal) {
      if (this.pageIndex > newTotal && newTotal > 0) {
        this.pageIndex = newTotal;
      } else if (newTotal === 0) {
        this.pageIndex = 1;
      }
    },
    currentSelectedRegion: {
      handler(newRegion) {
        this.pageIndex = 1;
        if (this.selectedTab !== 2) return;

        // Em cadastro: atualiza região/banca do formulário sem fechar
        if (!this.collector.id) {
          this.collector.regionId = newRegion?.id || null;
          if (newRegion) {
            const bancaAssociada = this.getBancaByRegion(newRegion.id);
            this.collector.bancaId = bancaAssociada?.id
              || newRegion.bancaId
              || this.selectedBancaSnapshot?.id
              || '';
          } else {
            this.collector.bancaId = this.selectedBancaSnapshot?.id || '';
          }
          return;
        }

        // Em edição: volta para listagem ao mudar o filtro
        this.reset();
      },
      deep: true
    },

    currentSelectedBanca: {
      handler() {
        this.pageIndex = 1;
        if (this.selectedTab !== 2) return;

        // Em cadastro: só atualiza a banca, sem fechar o formulário
        if (!this.collector.id) {
          if (this.currentSelectedRegion) {
            const bancaAssociada = this.getBancaByRegion(this.currentSelectedRegion.id);
            this.collector.bancaId = bancaAssociada?.id
              || this.currentSelectedRegion.bancaId
              || this.selectedBancaSnapshot?.id
              || '';
          } else {
            this.collector.bancaId = this.selectedBancaSnapshot?.id || '';
          }
          return;
        }

        this.reset();
      },
      deep: true
    },

    // Watch para auto-preencher banca quando região mudar no formulário
    'collector.regionId': {
      handler(newRegionId, oldRegionId) {
        if (newRegionId && newRegionId !== oldRegionId) {
          // Auto-preencher a banca baseada na região selecionada
          const bancaAssociada = this.getBancaByRegion(newRegionId);
          if (bancaAssociada) {
            this.collector.bancaId = bancaAssociada.id;
          } else {
            this.collector.bancaId = '';
          }
        }
      }
    }
  },
  methods: {
    readSelectedRegionFromStorage() {
      try {
        const savedRegion = localStorage.getItem('currentRegion');
        return savedRegion ? JSON.parse(savedRegion) : null;
      } catch {
        return null;
      }
    },

    readSelectedBancaFromStorage() {
      try {
        const savedBanca = localStorage.getItem('selectedBanca');
        return savedBanca ? JSON.parse(savedBanca) : null;
      } catch {
        return null;
      }
    },

    syncSelectedRegionSnapshot() {
      this.selectedRegionSnapshot = this.readSelectedRegionFromStorage();
    },

    syncSelectedBancaSnapshot() {
      this.selectedBancaSnapshot = this.readSelectedBancaFromStorage();
    },

    onRegionOrBancaChanged() {
      this.syncSelectedRegionSnapshot();
      this.syncSelectedBancaSnapshot();
    },

    onStorageChanged(event) {
      if (event.key === 'currentRegion') {
        this.syncSelectedRegionSnapshot();
      }
      if (event.key === 'selectedBanca') {
        this.syncSelectedBancaSnapshot();
      }
    },

    resolveCollectorBancaId() {
      this.syncSelectedRegionSnapshot();
      this.syncSelectedBancaSnapshot();

      if (this.collector?.bancaId) {
        return this.collector.bancaId;
      }

      const region = this.currentSelectedRegion;
      if (region?.bancaId) {
        return region.bancaId;
      }

      if (region?.id) {
        const bancaFromRegion = this.getBancaByRegion(region.id);
        if (bancaFromRegion?.id) {
          return bancaFromRegion.id;
        }
      }

      return this.selectedBancaSnapshot?.id || null;
    },

    async loadBancas() {
      const response = await mainService.searchBancas({ isActive: true });
      this.bancas = response.data || [];
    },

    async loadCurrentUser() {
      try {
        const userData = localStorage.getItem('user');
        if (userData) {
          this.currentUser = JSON.parse(userData);
          
          // Se não for ADMIN, carregar regiões do usuário responsável  
          if (this.currentUser.role !== 0) {
            await this.loadUserResponsibleRegions();
          }
        }
      } catch (error) {
        this.currentUser = null;
        toast.error("Erro ao carregar dados do usuário");
      }
    },

    async loadUserResponsibleRegions() {
      try {
        if (!this.currentUser) return;
        
        const response = await mainService.getUserRegions(this.currentUser.id);
        this.userResponsibleRegions = response.data.map(region => region.id);
        
        // Incluir a região principal também, se existir
        if (this.currentUser.regionId && !this.userResponsibleRegions.includes(this.currentUser.regionId)) {
          this.userResponsibleRegions.push(this.currentUser.regionId);
        }
      } catch (error) {
        this.userResponsibleRegions = [];
        toast.error("Erro ao carregar regiões do usuário");
      }
    },

    async loadBancaRegions() {
      try {
        if (!this.currentUser || !this.isBanca) return;
        
        // Buscar bancas disponíveis para o usuário BANCA
        const bancasForUser = this.availableBancasForUser;
        
        for (const banca of bancasForUser) {
          if (banca.regionIds && Array.isArray(banca.regionIds)) {
            // Adicionar todas as regiões desta banca às regiões responsáveis
            banca.regionIds.forEach(regionId => {
              if (!this.userResponsibleRegions.includes(regionId)) {
                this.userResponsibleRegions.push(regionId);
              }
            });
          }
        }
      } catch (error) {
        // Ignorar erro
      }
    },

    async initializeFilters() {
      // Carregar regiões para o filtro
      const regionsResponse = await mainService.searchRegions();
      this.regions = regionsResponse.data;
    },
    
    createCollector() {
      if (!this.canCreateEditDeleteCollectors) {
        toast.error("Você não tem permissão para criar cobradores!");
        return;
      }

      this.syncSelectedRegionSnapshot();
      this.syncSelectedBancaSnapshot();

      const region = this.currentSelectedRegion;

      this.collector = {
        id: null,
        name: '',
        username: '',
        phone: '',
        regionId: region?.id || null,
        password: '',
        active: true,
        bancaId: region?.bancaId || this.selectedBancaSnapshot?.id || ''
      };

      if (region) {
        const bancaAssociada = this.getBancaByRegion(region.id);
        if (bancaAssociada) {
          this.collector.bancaId = bancaAssociada.id;
        } else if (region.bancaId) {
          this.collector.bancaId = region.bancaId;
        } else if (this.selectedBancaSnapshot?.id) {
          this.collector.bancaId = this.selectedBancaSnapshot.id;
        }
      } else {
        // Abre o formulário mesmo sem região; o aviso aparece no próprio form
        this.showRegionSelectionMessage();
      }

      this.selectedTab = 2;
    },

    showRegionSelectionMessage() {
      if (this.isAdmin) {
        if (!this.currentSelectedBanca) {
          toast.info("Para criar um cobrador, primeiro selecione uma banca e depois uma região nos filtros localizados no topo da página.");
        } else {
          toast.info("Para criar um cobrador, selecione uma região no filtro localizado no topo da página.");
        }
      } else {
        toast.info("Entre em contato com o administrador para configurar sua região ativa.");
      }
    },
    
    async editCollector(collector) {
      if (!this.canCreateEditDeleteCollectors) {
        toast.error("Você não tem permissão para editar cobradores!");
        return;
      }

      this.collector = { 
        ...collector,
        bancaId: collector.bancaId || '', // Adicionar campo de banca
        password: '' // Não mostrar senha existente
      };
      
      this.selectedTab = 2;
    },
    
    reset() {
      this.collector = {};
      this.selectedTab = 1;
    },
    
    validateCollector() {
      if (!this.collector.name || this.collector.name.trim() === '') {
        toast.error("Por favor, preencha o nome!");
        return false;
      }
      if (!this.collector.username || this.collector.username.trim() === '') {
        toast.error("Por favor, preencha o nome de usuário!");
        return false;
      }
      
      // Verificar se já existe outro cobrador com o mesmo nome
      const existingCollectorByName = this.collectors.find(c => 
        c.name.toUpperCase().trim() === this.collector.name.toUpperCase().trim() && 
        c.id !== this.collector.id
      );
      if (existingCollectorByName) {
        toast.error("Já existe um cobrador cadastrado com este nome!");
        return false;
      }
      
      // Verificar se já existe outro cobrador com o mesmo username
      const existingCollectorByUsername = this.collectors.find(c => 
        c.username.toUpperCase() === this.collector.username.toUpperCase() && 
        c.id !== this.collector.id
      );
      if (existingCollectorByUsername) {
        toast.error("Já existe um cobrador cadastrado com este nome de usuário!");
        return false;
      }
      
      if (!this.collector.id && (!this.collector.password || this.collector.password.trim() === '')) {
        toast.error("Senha é obrigatória para novos cobradores!");
        return false;
      }
      // Para edição, a senha é opcional - se preenchida, deve ter pelo menos 3 caracteres
      if (this.collector.id && this.collector.password && this.collector.password.trim() !== '' && this.collector.password.length < 3) {
        toast.error("A nova senha deve ter pelo menos 3 caracteres!");
        return false;
      }
      return true;
    },
    
    async searchCollectors() {
      this.loading = true;
      try {
        const response = await mainService.searchCollectors();
        this.collectors = response.data.map(collector => {
          // Buscar a região do cobrador para obter a banca
          const collectorRegion = this.regions.find(r => r.id === collector.regionId);
          const collectorBanca = collectorRegion ? this.bancas.find(b => b.id === collectorRegion.bancaId) : null;
          
          return {
            id: collector.id,
            name: collector.name,
            username: collector.username,
            phone: collector.phone,
            regionId: collector.regionId,
            regionName: collector.regionName,
            bancaId: collectorBanca?.id || null, // ID da banca baseado na região
            bancaName: collectorBanca?.name || '-', // Nome da banca baseado na região
            active: collector.isActive
          };
        });
        this.pageIndex = 1;

        // Cobradores não têm regiões adicionais - apenas região principal
      } catch (error) {
        toast.error(handleErrorMessage(error));
      } finally {
        this.loading = false;
      }
    },

    changePage(newPage) {
      if (newPage >= 1 && newPage <= this.totalPages) {
        this.pageIndex = newPage;
      }
    },

    async loadAdditionalRegionsForCollectors() {
      // Carregar regiões adicionais para cada cobrador para o filtro funcionar corretamente
      const promises = this.collectors.map(async (collector) => {
        try {
          const response = await mainService.getUserRegions(collector.id);
          collector.additionalRegions = response.data || [];
        } catch (error) {
          collector.additionalRegions = [];
        }
      });

      await Promise.all(promises);
    },
    
    async insertCollector() {
      if (this.saving) return;
      this.saving = true;

      try {
        if (!this.canCreateEditDeleteCollectors) {
          toast.error("Você não tem permissão para criar cobradores!");
          return;
        }

        // Verificar se há região ativa selecionada
        this.syncSelectedRegionSnapshot();
        if (!this.currentSelectedRegion) {
          toast.error("É necessário selecionar uma região ativa para criar um cobrador!");
          this.showRegionSelectionMessage();
          return;
        }

        // Garantir que o cobrador está associado à região ativa e à banca
        this.collector.regionId = this.currentSelectedRegion.id;
        this.collector.bancaId = this.resolveCollectorBancaId();

        if (!this.collector.bancaId) {
          toast.error("Não foi possível determinar a banca. Selecione banca/região no cabeçalho!");
          return;
        }

        if (!this.validateCollector()) {
          return;
        }

        await mainService.insertCollector(this.collector);
        
        // Cobradores não têm regiões adicionais - apenas região principal
        
        toast.success("Cobrador cadastrado com sucesso!");
        this.reset();
        
        // Para usuários não-admin, recarregar regiões responsáveis antes de buscar cobradores
        if (!this.isAdmin) {
          await this.loadUserResponsibleRegions();
          if (this.isBanca) {
            await this.loadBancaRegions();
          }
        }
        
        await this.searchCollectors();
      } catch (error) {
        toast.error(handleErrorMessage(error));
      } finally {
        this.saving = false;
      }
    },
    
    async updateCollector() {
      if (this.saving) return;
      this.saving = true;

      try {
        if (!this.canCreateEditDeleteCollectors) {
          toast.error("Você não tem permissão para editar cobradores!");
          return;
        }

        if (!this.validateCollector()) {
          return;
        }

        // Preparar dados do cobrador para envio
        const collectorData = { ...this.collector };
        collectorData.bancaId = this.resolveCollectorBancaId();
        
        // Se a senha estiver vazia, removê-la do objeto para não alterar a senha atual
        if (!collectorData.password || collectorData.password.trim() === '') {
          delete collectorData.password;
        }
        
        await mainService.updateCollector(collectorData);
        
        // Cobradores não têm regiões adicionais - apenas região principal
        
        toast.success("Cobrador atualizado com sucesso!");
        this.reset();
        
        // Para usuários não-admin, recarregar regiões responsáveis antes de buscar cobradores
        if (!this.isAdmin) {
          await this.loadUserResponsibleRegions();
          if (this.isBanca) {
            await this.loadBancaRegions();
          }
        }
        
        await this.searchCollectors();
      } catch (error) {
        toast.error(handleErrorMessage(error));
      } finally {
        this.saving = false;
      }
    },
    
    deleteCollector(id) {
      if (!this.canCreateEditDeleteCollectors) {
        toast.error("Você não tem permissão para excluir cobradores!");
        return;
      }

      this.$confirm({
        message: "Confirma a exclusão do cobrador?",
        button: { no: "Não", yes: "Sim" },
        callback: async (confirm) => {
          if (confirm) {
            try {
              await mainService.deleteCollector(id);
              toast.success("Cobrador excluído com sucesso!");
              await this.searchCollectors();
            } catch (error) {
              toast.error(handleErrorMessage(error));
            }
          }
        },
      });
      setTimeout(() => {
        document.getElementsByClassName("vc-btn left")[0].focus();
      }, 1);
    },



    showPermissionError() {
      toast.error("Você não tem permissão para criar/editar cobradores!");
    },

    getBancaName(bancaId) {
      if (!bancaId) return 'Nenhuma banca associada à região';
      if (!this.bancas || this.bancas.length === 0) return 'Carregando bancas...';
      
      const banca = this.bancas.find(banca => banca.id === bancaId);
      return banca ? banca.name : 'Banca não encontrada';
    },

    getBancaByRegion(regionId) {
      // Encontrar a banca que tem essa região associada
      if (!regionId) return null;
      if (!this.bancas || this.bancas.length === 0) return null;

      // Preferir BancaId direto da região (fonte da verdade)
      const region = this.regions.find(r => String(r.id) === String(regionId));
      if (region?.bancaId) {
        const bancaByRegionFk = this.bancas.find(b => String(b.id) === String(region.bancaId));
        if (bancaByRegionFk) return bancaByRegionFk;
      }
      
      const banca = this.bancas.find(banca => {
        // Verificar se a região está nas regiões adicionais da banca
        if (banca.regionIds && banca.regionIds.includes(regionId)) {
          return true;
        }
        return false;
      });
      
      return banca || null;
    }
  },
};
</script>

<style scoped>
/* Campo obrigatório */
.input-label.required::after {
  content: " *";
  color: #dc3545;
}

/* Estilos para múltiplas regiões inline */
.input-group.full-width {
  grid-column: 1 / -1;
}

/* Mensagem de permissão */
.permission-info {
  padding: 7px;
  background-color: #3478f2;
  border: 1px solid #548fff;
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: 10px;
  color: white;
}

.info-card {
  display: flex;
  align-items: center;
  gap: 8px;
}

.info-content {
  font-size: 0.9em;
  line-height: 1.4;
}

/* Aba desabilitada */
.tab-button.disabled {
  opacity: 0.5;
  cursor: not-allowed !important;
  pointer-events: none;
}

.tab-button.disabled:hover {
  background-color: var(--bg-secondary) !important;
  color: var(--text-muted) !important;
}

/* Texto de ajuda para campos */
.input-help {
  display: block;
  margin-top: 5px;
  font-size: 0.8rem;
  color: #6c757d;
  line-height: 1.3;
}

[data-theme=dark] .input-help {
  color: #a8a8a8;
}

.input-help i {
  margin-right: 4px;
  font-size: 0.75rem;
}

/* Texto de ajuda adicional para formulários */
.form-help-text {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 6px;
  font-size: 0.85rem;
  color: var(--text-muted);
  line-height: 1.4;
}

.form-help-text i {
  color: var(--accent-primary);
  font-size: 0.875rem;
}

/* Estilos para região ativa */
.region-info-container {
  background: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  padding: 15px;
}

[data-theme=dark] .region-info-container {
  background: #1c1c1f;
  border: 1px solid #52525b;
}

.region-info-display {
  text-align: center;
}

.active-region {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  background: #d4edda;
  color: #155724;
  padding: 12px 20px;
  border-radius: 6px;
  border: 1px solid #c3e6cb;
  margin-bottom: 10px;
  font-weight: 500;
}

[data-theme=dark] .active-region {
  background: #0f2a1c;
  color: #4ade80;
  border: 1px solid #22c55e;
}

.active-region i {
  font-size: 1.1rem;
}

.region-name {
  font-size: 1rem;
  font-weight: 600;
}

.region-status {
  font-size: 0.85rem;
  opacity: 0.8;
}

.region-help {
  display: block;
  font-size: 0.8rem;
  color: #6c757d;
  text-align: center;
  line-height: 1.3;
}

[data-theme=dark] .region-help {
  color: #a8a8a8;
}

.region-help i {
  margin-right: 4px;
  font-size: 0.75rem;
}

.no-region-warning {
  text-align: center;
}

.warning-content {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  background: #fff3cd;
  color: #856404;
  padding: 15px 20px;
  border-radius: 6px;
  border: 1px solid #ffeaa7;
  margin-bottom: 15px;
}

[data-theme=dark] .warning-content {
  background: #2d1810;
  color: #f59e0b;
  border: 1px solid #f59e0b;
}

.warning-content i {
  font-size: 1.2rem;
  flex-shrink: 0;
}

.warning-text {
  text-align: left;
}

.warning-text strong {
  display: block;
  margin-bottom: 5px;
  font-size: 0.95rem;
}

.warning-text p {
  margin: 0;
  font-size: 0.85rem;
  line-height: 1.4;
}

.btn-select-region {
  background: #007bff;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.9rem;
  font-weight: 500;
  transition: background-color 0.2s;
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0 auto;
}

.btn-select-region:hover {
  background: #0056b3;
}

.btn-select-region i {
  font-size: 0.9rem;
}

/* Estilos para o campo de banca auto-preenchido */
.banca-auto-filled {
  background-color: #e9ecef !important;
  border: 1px solid #ced4da !important;
  color: #6c757d !important;
  cursor: not-allowed;
}

[data-theme=dark] .banca-auto-filled {
  background-color: #2d3748 !important;
  border: 1px solid #4a5568 !important;
  color: #a0aec0 !important;
}

/* Estilos melhorados para mensagem de nenhum dado */
.no-data-content {
  display: flex;
  flex-direction: column;
  gap: 8px;
  text-align: center;
}

.no-data-title {
  font-weight: 600;
  font-size: 1rem;
  color: var(--text-primary);
}

.no-data-subtitle {
  font-size: 0.875rem;
  color: var(--text-muted);
  line-height: 1.4;
}

[data-theme=dark] .no-data-title {
  color: #e2e8f0;
}

[data-theme=dark] .no-data-subtitle {
  color: #a0aec0;
}

.table-count {
  font-size: 0.875rem;
  color: var(--text-secondary);
  font-weight: 400;
  white-space: nowrap;
}

.pagination-info {
  display: flex;
  align-items: center;
  gap: 1rem;
  font-size: 0.875rem;
  color: var(--text-secondary);
}

.pagination-controls {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.btn-pagination {
  padding: 0.5rem;
  border: 1px solid var(--card-border, var(--border-color));
  background: var(--bg-primary);
  color: var(--text-primary);
  border-radius: 6px;
  cursor: pointer;
  transition: var(--transition, all 0.2s);
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
}

.btn-pagination:hover:not(:disabled) {
  background: var(--accent-primary);
  color: white;
  border-color: var(--accent-primary);
}

.btn-pagination:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.page-info {
  font-weight: 500;
  color: var(--text-primary);
  min-width: 60px;
  text-align: center;
}

.pagination-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  border-top: 1px solid var(--card-border, var(--border-color));
  background: var(--bg-primary);
}

@media (max-width: 768px) {
  .pagination-footer {
    flex-direction: column;
    gap: 1rem;
  }
}
</style> 